import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, PlusCircle, Upload, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KpiCard } from "@/components/KpiCard";
import { SeasonBadge, Tabs } from "@/components/tyres/common";
import { BaseStockDialog } from "@/components/tyres/BaseStockDialog";
import { TyreImportDialog } from "@/components/tyres/TyreImportDialog";
import { api, errMsg } from "@/lib/api";

const Qty = ({ v }) => <span className={`font-mono-num font-semibold ${v < 0 ? "text-rose-600" : v === 0 ? "text-slate-400" : "text-slate-900"}`}>{v}</span>;

const AdjustDialog = ({ sid, isAdmin, types, onClose, onSaved }) => {
  const [f, setF] = useState({ type_id: "", condition: "NUOVA", sign: "+", qty: "", note: "" });
  const save = async () => {
    try {
      await api.post("/tyres/stock/adjust", { station_id: isAdmin ? sid : undefined, type_id: f.type_id, condition: f.condition, qty: Number(f.qty) * (f.sign === "-" ? -1 : 1), note: f.note });
      toast.success("Magazzino aggiornato"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="tyre-adjust-dialog">
        <DialogHeader><DialogTitle>Carico / scarico manuale</DialogTitle><DialogDescription>Per la giacenza iniziale o per correggere il magazzino.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Gomma</Label>
            <Select value={f.type_id || undefined} onValueChange={(v) => setF({ ...f, type_id: v })}>
              <SelectTrigger data-testid="tyre-adjust-type"><SelectValue placeholder="Seleziona gomma" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex flex-wrap gap-2">
            <Tabs value={f.sign} onChange={(v) => setF({ ...f, sign: v })} items={[["+", "Carico"], ["-", "Scarico"]]} prefix="tyre-adjust-sign" />
            <Tabs value={f.condition} onChange={(v) => setF({ ...f, condition: v })} items={[["NUOVA", "Nuove"], ["USATA", "Usate"]]} prefix="tyre-adjust-cond" />
          </div>
          <div className="space-y-1.5"><Label>Quantità</Label><Input type="number" min="1" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="tyre-adjust-qty" /></div>
          <div className="space-y-1.5"><Label>Nota</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="tyre-adjust-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} disabled={!f.type_id || !(Number(f.qty) > 0)} data-testid="tyre-adjust-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StockTab = ({ sid, isAdmin, types, tick, refresh }) => {
  const [data, setData] = useState({ items: [], moves: [], totals: {} });
  const [adjust, setAdjust] = useState(false);
  const [base, setBase] = useState(false);
  const [imp, setImp] = useState(false);
  useEffect(() => { api.get("/tyres/stock", { params: { station_id: isAdmin ? sid : undefined } }).then((r) => setData(r.data)); }, [sid, isAdmin, tick]);
  const t = data.totals;
  return (
    <>
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Nuove a magazzino" value={t.NUOVA ?? 0} accent="#059669" testId="tyre-kpi-new" />
        <KpiCard label="Usate a magazzino" value={t.USATA ?? 0} accent="#D97706" testId="tyre-kpi-used" />
        <KpiCard label="In ordine" value={t.on_order ?? 0} accent="#2563EB" testId="tyre-kpi-order" />
        <KpiCard label="Sotto scorta minima" value={t.below ?? 0} accent="#DC2626" testId="tyre-kpi-below" />
      </div>
      {t.below > 0 && <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800" data-testid="tyre-below-alert">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {t.below} {t.below === 1 ? "gomma è" : "gomme sono"} sotto la scorta minima: valuta un ordine.</div>}
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setImp(true)} data-testid="tyre-stock-import-button"><Upload className="mr-2 h-4 w-4" /> Importa magazzino</Button>
        <Button variant="outline" onClick={() => setBase(true)} disabled={!types.length} data-testid="tyre-base-button"><Warehouse className="mr-2 h-4 w-4" /> Magazzino di base</Button>
        <Button variant="outline" onClick={() => setAdjust(true)} disabled={!types.length} data-testid="tyre-adjust-button"><PlusCircle className="mr-2 h-4 w-4" /> Carico / scarico manuale</Button>
      </div>
      <div className="mb-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="tyre-stock-table">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap [&_th]:px-3 [&_th]:py-2">
            <tr><th>Gomma</th><th>Stagione</th><th className="text-right">Nuove</th><th className="text-right">Usate</th><th className="text-right">In ordine</th><th className="text-right">Scorta min.</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.items.map((r) => (
              <tr key={r.type_id} className={r.below ? "bg-rose-50" : ""} data-testid={`tyre-stock-row-${r.type_id}`}>
                <td className="px-3 py-2 font-medium">{r.label.split(" · ")[0]}</td><td className="px-3 py-2"><SeasonBadge season={r.season} /></td>
                <td className="px-3 py-2 text-right"><Qty v={r.NUOVA} /></td><td className="px-3 py-2 text-right"><Qty v={r.USATA} /></td>
                <td className="px-3 py-2 text-right"><Qty v={r.on_order} /></td>
                <td className="whitespace-nowrap px-3 py-2 text-right">{r.min ? <span className={`font-mono-num font-semibold ${r.below ? "text-rose-600" : "text-slate-600"}`} data-testid={`tyre-stock-min-${r.type_id}`}>{r.below && <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />}{r.min}</span> : <span className="text-slate-300">—</span>}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">Magazzino vuoto: usa "Magazzino di base" o "Importa magazzino" per caricare la giacenza.</td></tr>}
          </tbody>
        </table>
      </div>
      <h3 className="mb-2 text-sm font-semibold text-slate-700">Ultimi movimenti</h3>
      <div className="max-h-96 overflow-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="tyre-moves-table">
          <tbody className="divide-y">
            {data.moves.map((m) => (
              <tr key={m.id}>
                <td className="whitespace-nowrap px-3 py-2">{new Date(m.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="whitespace-nowrap px-3 py-2 font-semibold">{m.reason}</td>
                <td className="px-3 py-2">{m.type_label.split(" · ")[0]} · {m.condition === "NUOVA" ? "nuove" : "usate"}</td>
                <td className={`font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold ${m.qty < 0 ? "text-rose-600" : "text-emerald-700"}`}>{m.qty > 0 ? `+${m.qty}` : m.qty}</td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-500">{[m.plate, m.note, m.by_name].filter(Boolean).join(" · ")}</td>
              </tr>
            ))}
            {!data.moves.length && <tr><td className="px-3 py-6 text-center text-slate-500">Nessun movimento.</td></tr>}
          </tbody>
        </table>
      </div>
      {base && <BaseStockDialog sid={sid} isAdmin={isAdmin} types={types} stock={data.items} onClose={() => setBase(false)} onSaved={() => { setBase(false); refresh(); }} />}
      {imp && <TyreImportDialog kind="stock" sid={sid} isAdmin={isAdmin} onClose={() => setImp(false)} onDone={() => { setImp(false); refresh(); }} />}
      {adjust && <AdjustDialog sid={sid} isAdmin={isAdmin} types={types} onClose={() => setAdjust(false)} onSaved={() => { setAdjust(false); refresh(); }} />}
    </>
  );
};
